'use client';

import { c as _c } from "react/compiler-runtime";
import lazy from 'next/dynamic';
import { provideGlobalGridOptions } from 'ag-grid-community';
import { LicenseInfo } from '@mui/x-license';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v16-appRouter';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import PendingChangesProvider from '@/contexts/PendingChangesContext';
import Toaster from '@/components/Toaster';
import { PostHogProvider } from '@/modules/posthog/client';
import PrimeReactProvider from './PrimeReactProvider';
import type { ReactNode } from 'react';
const SystemAlert = lazy(() => import('@/components/SystemAlert'), {
  ssr: false
});
LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_LICENSE || '');

// AG Grid injects its theme CSS unlayered by default, which outranks every
// cascade layer — including the app's `[&_.ag-*]` overrides that live in
// @layer utilities since Tailwind v4 (v3 emitted them unlayered, so they could
// still compete). Route AG styles into the `grid` layer (declared below
// utilities in global.css) for every grid, so app overrides win again.
provideGlobalGridOptions({
  themeCssLayer: 'grid'
});
const baseMuiTheme = createTheme({
  typography: {
    fontFamily: 'inherit'
  }
});
const WithPostHog = t0 => {
  const $ = _c(2);
  const {
    children,
    withPostHog
  } = t0;
  if (withPostHog) {
    let t1;
    if ($[0] !== children) {
      t1 = <PostHogProvider>{children}</PostHogProvider>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  return children;
};
export default function RootProviders(t0) {
  const $ = _c(12);
  const {
    children,
    withPostHog,
    withStatusWidget
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      enableCssLayer: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <PendingChangesProvider>{children}</PendingChangesProvider>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2 || $[4] !== withPostHog) {
    t3 = <PrimeReactProvider><WithPostHog withPostHog={withPostHog}>{t2}</WithPostHog></PrimeReactProvider>;
    $[3] = t2;
    $[4] = withPostHog;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== withStatusWidget) {
    t4 = withStatusWidget && <SystemAlert />;
    $[6] = withStatusWidget;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Toaster />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4) {
    t6 = <AppRouterCacheProvider options={t1}><ThemeProvider theme={baseMuiTheme}>{t3}{t4}{t5}</ThemeProvider></AppRouterCacheProvider>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}